import api from "../../Services/api";

export const fetchAllForms = async () => {
  const response = await api.get("/forms/all");

  const processedForms = response.data.forms
    .map((form) => {
      if (!form.properties) return form;

      const updatedProperties = Object.fromEntries(
        Object.entries(form.properties).filter(
          ([_, prop]) => prop.updated === true
        )
      );

      // Exclude forms with no updated properties
      if (Object.keys(updatedProperties).length === 0) {
        return null;
      }

      return {
        ...form,
        properties: updatedProperties,
      };
    })
    .filter(Boolean);
  return processedForms;
};

export const fetchUserCreatedForms = async () => {
  try {
    // Get user data from localStorage and ensure it exists
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    // Check if userData and id exist
    if (!userData || !userData.id) {
      console.error("User data not found in localStorage");
      return [];
    }

    // Convert userId to string explicitly to match backend expectations
    const userId = String(userData.id);

    // Fetch forms created by the current logged-in user
    const response = await api.get(`/forms/all/${userId}`);
    return response.data.forms || [];
  } catch (error) {
    console.error("Error fetching user forms:", error);
    // If 403 or 204, it's not a critical error, just return empty forms
    if (
      error.response &&
      (error.response.status === 403 || error.response.status === 204)
    ) {
      return [];
    }
    throw error;
  }
};

export const fetchFilledForms = async (formId) => {
  const response = await api.get(`/filled-forms/formSchemaId/${formId}`);
  return response.data.filledForms || [];
};

export const fetchSuperuserFilters = async () => {
  try {
    const response = await api.get("/forms/filter/rules/user");

    // Check if response.data.filters exists and is an array
    if (!response.data || !Array.isArray(response.data.filters)) {
      console.warn("No filters found in response:", response.data);
      return [];
    }

    // Group rules by ruleName and formId
    const groupedFilters = response.data.filters.reduce((acc, filter) => {
      const key = `${filter.formId}-${filter.ruleName}`;

      if (!acc[key]) {
        acc[key] = {
          name: filter.ruleName,
          formId: filter.formId,
          formTitle: filter.formLabel,
          rules: [],
        };
      }

      acc[key].rules.push({
        id: filter.ruleId,
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
      });

      return acc;
    }, {});

    return Object.values(groupedFilters);
  } catch (error) {
    console.error("Error fetching superuser filters:", error);
    // Return empty array if 404 or no content
    if (
      error.response &&
      (error.response.status === 404 || error.response.status === 204)
    ) {
      return [];
    }
    throw error;
  }
};

export const fetchAllFilters = async () => {
  try {
    const response = await api.get(`/forms/filter/rules/all`);

    // Check if response.data.filters exists and is an array
    if (!response.data || !Array.isArray(response.data.filters)) {
      console.warn("No filters found in response:", response.data);
      return [];
    }

    // Group rules by ruleName and formId
    const groupedFilters = response.data.filters.reduce((acc, filter) => {
      const key = `${filter.formId}-${filter.ruleName}`;

      if (!acc[key]) {
        acc[key] = {
          name: filter.ruleName,
          formId: filter.formId,
          formTitle: filter.formLabel,
          rules: [],
        };
      }

      acc[key].rules.push({
        id: filter.ruleId,
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
      });

      return acc;
    }, {});

    return Object.values(groupedFilters);
  } catch (error) {
    console.error("Error fetching all filters:", error);
    // Return empty array if 404 or no content
    if (
      error.response &&
      (error.response.status === 404 || error.response.status === 204)
    ) {
      return [];
    }
    throw error;
  }
};

export const addFilter = async (id, filterData) => {
  const response = await api.post(`/forms/filter/rules/${id}`, filterData);
  return response.data;
};

export const deleteFilter = async (formId, ruleId) => {
  const response = await api.delete(`/forms/filter/rules/${formId}/${ruleId}`);
  return response.data;
};

export const deleteAllFilters = async (formId) => {
  const response = await api.delete(`/forms/filter/rules/${formId}`);
  return response.data;
};

export const editFilter = async (formId, ruleId, filterData) => {
  const response = await api.put(
    `/forms/filter/rules/${formId}/${ruleId}`,
    filterData
  );
  return response.data;
};

export const fetchFilterPreview = async (formId, filterData) => {
  const response = await api.post(
    `/forms/filter/preview/${formId}`,
    filterData
  );
  return response.data;
};
